import { useState, useEffect, useMemo } from 'react';
import logoMark from '../assets/logo-gestion360.png';
import { BRAND } from '../config/brand';
import { requestTrial, getPublicPlans, getSignupInfo, selfSignup } from '../lib/org';
import { FEATURE_GROUPS, hasFeature, includedList } from '../lib/plans';

const PLANS = [
  { id: 'esencial', name: 'Esencial', who: 'Para empezar', people: 'Hasta 15 personas', places: '1 sede', items: ['Marcación de entrada y salida', 'Control de horarios y tardanzas', 'Licencias y vacaciones'] },
  { id: 'profesional', name: 'Profesional', who: 'El más elegido', people: 'Hasta 50 personas', places: 'Hasta 3 sedes', items: ['Todo lo de Esencial', 'Liquidación de haberes y recibos', 'Exportación de reportes'], hot: true },
  { id: 'business', name: 'Business', who: 'Para equipos grandes', people: 'Hasta 200 personas', places: 'Hasta 10 sedes', items: ['Todo lo de Profesional', 'Mails con el nombre de tu organización', 'Reportes mensuales automáticos'] },
  { id: 'total', name: 'Total', who: 'Sin límites', people: 'Personas ilimitadas', places: 'Sedes ilimitadas', items: ['Todo lo de Business', 'Soporte prioritario', 'Acompañamiento en la puesta en marcha'] },
];

const fmtPrice = (p) => {
  if (!p || p.price_amount == null) return null;
  const n = Number(p.price_amount).toLocaleString('es-AR', { maximumFractionDigits: 0 });
  return `${p.price_currency === 'USD' ? 'US$' : '$'} ${n}`;
};
const grad = { background: 'var(--brand-grad)' };

// Íconos de línea (sin emojis)
const ICONS = {
  coin: 'M12 6v12M9 9.5c0-1.1 1.3-2 3-2s3 .9 3 2-1.3 2-3 2-3 .9-3 2 1.3 2 3 2 3-.9 3-2M12 3a9 9 0 100 18 9 9 0 000-18z',
  clock: 'M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z',
  shield: 'M12 3l8 3v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6l8-3zM9 12l2 2 4-4',
  eye: 'M2.5 12C3.5 10 7 6 12 6s8.5 4 9.5 6c-1 2-4.5 6-9.5 6S3.5 14 2.5 12zM12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z',
  pin: 'M12 21s6-5.2 6-10a6 6 0 10-12 0c0 4.8 6 10 6 10zM12 8.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z',
  qr: 'M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h2v2h-2v-2zm4 0h2v6h-4M14 18h2',
  tablet: 'M7 3h10a1 1 0 011 1v16a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1zm5 15h.01',
  finger: 'M12 11v4m-4-3a4 4 0 118 0v3m-12-2a8 8 0 1116 0v2M9 20c.5-1.5 1-3 1-5',
  moon: 'M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z',
  cal: 'M4 7a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm0 3h16M8 3v4m8-4v4',
  doc: 'M7 3h7l5 5v13H7V3zm7 0v5h5M10 13h6M10 17h6',
  calc: 'M6 3h12a1 1 0 011 1v16a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1zm2 4h8M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01M16 16h.01',
  bell: 'M6 17V11a6 6 0 1112 0v6l1.5 2h-15L6 17zm4 4h4',
  chart: 'M4 20V10m6 10V4m6 16v-7m4 7H3',
  build: 'M4 21V6l8-3 8 3v15M9 21v-5h6v5M9 9h.01M12 9h.01M15 9h.01M9 12.5h.01M12 12.5h.01M15 12.5h.01',
  mail: 'M3 6h18v12H3V6zm0 1l9 7 9-7',
  book: 'M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3V4zm0 13a3 3 0 013-3h11',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  x: 'M6 6l12 12M18 6L6 18',
  phone: 'M8 3h8a1 1 0 011 1v16a1 1 0 01-1 1H8a1 1 0 01-1-1V4a1 1 0 011-1zm4 16h.01',
};
function Icon({ name, className = 'w-5 h-5' }) {
  return <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d={ICONS[name]} /></svg>;
}

const BENEFITS = [
  { icon: 'coin', title: 'Ahorrás plata', text: 'Pagás lo que realmente se trabajó. Llegadas tarde, ausencias y horas extra quedan registradas con hora y lugar, y el descuento se calcula con las reglas que vos definís, sin errores de planilla ni discusiones a fin de mes.' },
  { icon: 'clock', title: 'Ganás tiempo', text: 'Se terminó juntar papeles, planillas y mensajes de WhatsApp. Los horarios, las licencias y la liquidación se arman solos a partir de las marcaciones; lo que antes llevaba días se revisa en minutos.' },
  { icon: 'shield', title: 'Tranquilidad', text: 'Todo queda registrado: quién marcó, cuándo y desde dónde, quién aprobó cada licencia y quién cambió cada dato. Si hay un reclamo, tenés el historial a mano.' },
  { icon: 'eye', title: 'Claridad para todos', text: 'Cada empleado ve su propio estado: horas, licencias, vacaciones y faltas justificadas. Reglas claras desde el primer día y menos malentendidos entre el equipo y la conducción.' },
];

const FEATURES_INFO = [
  { icon: 'pin', title: 'Marcación con ubicación', text: 'Entrada y salida desde el celular, validadas por la ubicación de la sede. Sabés dónde y cuándo se marcó.' },
  { icon: 'qr', title: 'QR en pantalla', text: 'Una pantalla en la entrada muestra un código que cambia solo; el equipo lo escanea y marca en segundos.' },
  { icon: 'tablet', title: 'Kiosco con código personal', text: 'Una tablet fija en la puerta donde cada persona marca con su código, sin usar su celular.' },
  { icon: 'finger', title: 'Huella o rostro del celular', text: 'Verificación biométrica del propio teléfono para asegurarte de que marca quien corresponde.' },
  { icon: 'moon', title: 'Turnos nocturnos', text: 'Un turno de 18 a 02 se registra completo como una sola jornada, sin cortes por el cambio de día.' },
  { icon: 'cal', title: 'Horarios y tolerancias', text: 'Horarios por persona y por día, tolerancia configurable y feriados de la organización.' },
  { icon: 'doc', title: 'Licencias, vacaciones y certificados', text: 'Pedidos y aprobaciones en la app. Las ausencias se justifican adjuntando el certificado, y vos decidís si descuentan o no.' },
  { icon: 'calc', title: 'Liquidación y recibos', text: 'Sueldos, horas extra, descuentos y recibos calculados a partir de lo que realmente pasó en el mes.' },
  { icon: 'bell', title: 'Alertas automáticas', text: 'Aviso cuando alguien acumula tardanzas o ausencias, o cuando hay vacaciones por vencer.' },
  { icon: 'chart', title: 'Reportes y exportación', text: 'Totales de horas, sueldos y personas, con filtros por fecha, sede y empleado, y descarga de los datos.' },
  { icon: 'build', title: 'Varias sedes', text: 'Cada persona asignada a su sede, con ubicación y red propias, y todo consolidado en un solo lugar.' },
  { icon: 'mail', title: 'Mails con tu nombre', text: 'Las comunicaciones salen con el nombre de tu organización y las respuestas llegan a tu casilla.' },
  { icon: 'book', title: 'Guía para el empleado', text: 'La primera vez que ingresa, cada persona recibe una guía paso a paso que puede volver a ver cuando quiera.' },
  { icon: 'phone', title: 'Se instala como app', text: 'Funciona en cualquier celular desde el navegador y se puede agregar a la pantalla de inicio. Sin tiendas de aplicaciones.' },
];

const STEPS = [
  ['1', 'Armamos tu organización', 'Cargás tu equipo, tus sedes y tus horarios. Cada organización tiene su propio enlace de ingreso, el mismo para administradores y empleados.'],
  ['2', 'Tu equipo marca desde donde prefiera', 'Celular, QR o kiosco. La app registra hora y lugar, y avisa cuando algo no coincide con el horario.'],
  ['3', 'Revisás y liquidás', 'Totales, licencias, justificaciones y sueldos listos para revisar. Aprobás y descargás los recibos.'],
];

const FAQ = [
  ['¿Mi equipo tiene que instalar algo?', 'No. Funciona desde el navegador del celular y, si quieren, la agregan a la pantalla de inicio como cualquier app.'],
  ['¿Cómo entran los empleados?', 'Cada organización tiene su propio enlace, por ejemplo gestion360.pages.dev/o/tu-organizacion. Ahí entran tanto los administradores como los empleados, cada uno con su correo y su clave.'],
  ['¿Qué pasa si alguien se olvida de marcar la salida?', 'La administración puede cargarla manualmente, y el cambio queda registrado con quién lo hizo.'],
  ['¿Puedo definir mis propias reglas de descuento?', 'Sí. Tolerancia, descuento por tardanza, valor de la ausencia y si las faltas justificadas descuentan o no se configuran por organización, y se pueden ajustar por empleado.'],
  ['¿Funciona con turnos que cruzan la medianoche?', 'Sí. Un turno de 18 a 02 se cuenta como una sola jornada.'],
  ['¿Puedo cambiar de plan después?', 'Sí, el plan se puede cambiar cuando tu organización lo necesite.'],
  ['¿Qué pasa con mis datos?', 'Cada organización ve únicamente sus propios datos. Podés pedir un respaldo completo cuando lo necesites. Encontrás el detalle en la Política de Privacidad.'],
];

export default function PricingPage({ onLogin }) {
  const [db, setDb] = useState({});
  useEffect(() => { getPublicPlans().then((l) => setDb(Object.fromEntries(l.map((x) => [x.id, x])))).catch(() => {}); }, []);
  const dbList = useMemo(() => Object.values(db), [db]);
  const plans = dbList.length ? dbList.map((d) => ({
    id: d.id, name: d.name, who: d.who || '', hot: !!d.hot, features: d.features,
    people: d.max_employees ? `Hasta ${d.max_employees} personas` : 'Personas ilimitadas',
    places: d.max_locations ? (d.max_locations === 1 ? '1 sede' : `Hasta ${d.max_locations} sedes`) : 'Sedes ilimitadas',
    items: [...(d.description ? [d.description] : []), ...includedList(d.features).slice(0, 7)],
    price: fmtPrice(d), note: d.price_note,
  })) : PLANS;
  const [form, setForm] = useState({ name: '', org: '', email: '', phone: '', plan: 'profesional', message: '' });
  const [state, setState] = useState({ busy: false, done: false, err: '' });
  const [signup, setSignup] = useState({ open: false, days: 14 });
  useEffect(() => { getSignupInfo().then(setSignup).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, err: '' });
    const plan = plans.some((p) => p.id === form.plan) ? form.plan : plans[0]?.id;
    try {
      if (signup.open) await selfSignup({ ...form, plan });
      else await requestTrial({ ...form, plan });
      setState({ busy: false, done: true, err: '' });
    }
    catch (ex) { setState({ busy: false, done: false, err: ex.message || 'No pudimos enviar el pedido' }); }
  };
  const pick = (id) => { setForm({ ...form, plan: id }); goTo('prueba'); };
  const input = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200';
  const compare = dbList.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      {/* Hero */}
      <header style={{ background: 'linear-gradient(160deg,#0b1224 0%,#1e293b 55%,#222d5a 100%)' }}>
        <div className="max-w-6xl mx-auto px-5 pt-6 pb-16 sm:pb-24">
          <nav className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <img src={logoMark} alt={BRAND.product} className="w-9 h-9 rounded-2xl" />
              <span className="text-white font-bold tracking-tight">{BRAND.product}</span>
            </div>
            <div className="hidden md:flex items-center gap-6 text-sm text-white/70">
              <button onClick={() => goTo('beneficios')} className="hover:text-white">Beneficios</button>
              <button onClick={() => goTo('funciones')} className="hover:text-white">Funciones</button>
              <button onClick={() => goTo('precios')} className="hover:text-white">Precios</button>
              <button onClick={() => goTo('preguntas')} className="hover:text-white">Preguntas</button>
            </div>
            <button onClick={onLogin} className="text-xs font-bold text-white/90 px-4 py-2 rounded-xl border border-white/20 hover:bg-white/10">Ingresar</button>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center mt-14 sm:mt-20">
            <div>
              <p className="inline-block text-[11px] font-bold uppercase tracking-widest text-indigo-300 bg-white/5 border border-white/10 rounded-full px-3 py-1">Control de personal para organizaciones, pymes y comercios</p>
              <h1 className="text-white text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-5">Pagá lo que se trabajó. <span className="text-indigo-300">Sin planillas ni discusiones.</span></h1>
              <p className="text-slate-300 mt-5 text-base sm:text-lg max-w-xl leading-relaxed">Gestión360 registra la asistencia de tu equipo con hora y lugar, ordena licencias y vacaciones, y calcula la liquidación. Ahorrás plata, ganás tiempo y tenés todo respaldado.</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <button onClick={() => goTo('prueba')} className="px-6 py-3 rounded-2xl text-sm font-bold text-slate-900 bg-white hover:bg-slate-100">{signup.open ? 'Crear cuenta gratis' : 'Probalo gratis'}</button>
                <button onClick={() => goTo('precios')} className="px-6 py-3 rounded-2xl text-sm font-bold text-white border border-white/25 hover:bg-white/10">Ver planes y precios</button>
              </div>
            </div>
            {/* Vista previa ilustrativa */}
            <div className="relative hidden sm:block" aria-hidden="true">
              <div className="rounded-3xl bg-white/5 border border-white/10 p-5 backdrop-blur">
                <div className="rounded-2xl bg-white p-5 shadow-2xl">
                  <div className="flex items-center justify-between"><p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Resumen del mes</p><span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ejemplo</span></div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {[['Personas', '24'], ['Horas', '3.912'], ['Justificadas', '6']].map(([l, v]) => (
                      <div key={l} className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] text-gray-400 uppercase font-bold">{l}</p><p className="text-lg font-black text-gray-900">{v}</p></div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    {[['Entrada 08:58 · Sede Centro', 'A tiempo', 'bg-emerald-100 text-emerald-700'], ['Entrada 09:21 · Sede Centro', 'Tardanza', 'bg-amber-100 text-amber-700'], ['Ausencia · certificado cargado', 'Justificada', 'bg-indigo-100 text-indigo-700']].map(([t, b, c]) => (
                      <div key={t} className="flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2.5 text-xs text-gray-600"><span>{t}</span><span className={`font-bold px-2 py-0.5 rounded-full ${c}`}>{b}</span></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Beneficios */}
      <section id="beneficios" className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-500">Por qué {BRAND.product}</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">Menos desorden. Más control. Más tiempo para lo importante.</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-10">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-white" style={grad}><Icon name={b.icon} /></span>
              <h3 className="text-lg font-black mt-4">{b.title}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <p className="text-xs font-bold uppercase tracking-widest text-indigo-500">Cómo funciona</p>
        <h2 className="text-3xl font-black tracking-tight mt-2">Funcionando en tres pasos</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {STEPS.map(([n, t, d]) => (
            <div key={n} className="relative bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <span className="text-5xl font-black text-indigo-100 absolute top-3 right-5">{n}</span>
              <h3 className="font-black text-lg pr-10">{t}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Funciones */}
      <section id="funciones" className="bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-500">Funciones</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">Todo lo que necesitás para gestionar a tu equipo</h2>
            <p className="text-gray-500 mt-3 text-sm">Según el plan que elijas, vas a tener algunas o todas estas funciones. Abajo, la comparación completa.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES_INFO.map((f) => (
              <div key={f.title} className="rounded-2xl border border-gray-100 bg-slate-50/60 p-5">
                <span className="text-indigo-600"><Icon name={f.icon} className="w-6 h-6" /></span>
                <h3 className="font-bold mt-3">{f.title}</h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Precios */}
      <section id="precios" className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-500">Planes y precios</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">Elegí el plan que acompaña a tu equipo</h2>
          <p className="text-gray-500 mt-3 text-sm">Empezá con una prueba gratuita y cambiá de plan cuando lo necesites.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {plans.map((p) => (
            <div key={p.id} className={`relative bg-white rounded-3xl p-6 shadow-sm border flex flex-col ${p.hot ? 'border-indigo-300 ring-2 ring-indigo-100 lg:-mt-3 lg:mb-3' : 'border-gray-100'}`}>
              {p.hot && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={grad}>El más elegido</span>}
              <p className="text-[11px] font-bold text-indigo-500 uppercase tracking-wide">{p.hot ? 'Ideal para la mayoría' : p.who}</p>
              <h3 className="text-xl font-black mt-1">{p.name}</h3>
              <p className="mt-3 text-gray-900">
                {p.price ? <><span className="text-3xl font-black tracking-tight">{p.price}</span> <span className="text-xs text-gray-400">{p.note || 'por mes'}</span></> : <span className="text-sm font-bold text-gray-500">Consultá el precio</span>}
              </p>
              <p className="text-sm font-semibold text-gray-700 mt-4">{p.people}</p>
              <p className="text-sm text-gray-500">{p.places}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {p.items.map((i) => <li key={i} className="text-xs text-gray-600 flex gap-2"><span className="text-emerald-500 mt-0.5"><Icon name="check" className="w-3.5 h-3.5" /></span>{i}</li>)}
              </ul>
              <button onClick={() => pick(p.id)} className="mt-6 w-full py-3 rounded-xl text-sm font-bold text-white" style={grad}>{signup.open ? 'Empezar con' : 'Probar'} {p.name}</button>
            </div>
          ))}
        </div>

        {compare && (
          <div className="mt-14">
            <h3 className="text-xl font-black text-center">Comparación de planes</h3>
            <div className="mt-6 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left p-4 w-1/3" />
                    {plans.map((p) => <th key={p.id} className={`p-4 text-center ${p.hot ? 'bg-indigo-50/60' : ''}`}><span className="font-black">{p.name}</span><span className="block text-xs font-semibold text-gray-400">{p.price ? `${p.price} ${p.note || '/ mes'}` : 'Consultá'}</span></th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-50"><td className="p-4 font-semibold text-gray-700">Personas</td>{plans.map((p) => <td key={p.id} className={`p-4 text-center text-gray-600 ${p.hot ? 'bg-indigo-50/40' : ''}`}>{p.people.replace('Hasta ', '').replace(' personas', '')}</td>)}</tr>
                  <tr className="border-b border-gray-100"><td className="p-4 font-semibold text-gray-700">Sedes</td>{plans.map((p) => <td key={p.id} className={`p-4 text-center text-gray-600 ${p.hot ? 'bg-indigo-50/40' : ''}`}>{p.places.replace('Hasta ', '').replace(' sedes', '').replace(' sede', '')}</td>)}</tr>
                  {FEATURE_GROUPS.map((g) => [
                    <tr key={g.id} className="bg-slate-50"><td colSpan={plans.length + 1} className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">{g.label}</td></tr>,
                    ...g.items.map((f) => (
                      <tr key={f.key} className="border-b border-gray-50 last:border-0">
                        <td className="p-4 text-gray-700">{f.label}</td>
                        {plans.map((p) => (
                          <td key={p.id} className={`p-4 text-center ${p.hot ? 'bg-indigo-50/40' : ''}`}>
                            {hasFeature(p.features, f.key)
                              ? <span className="inline-flex text-emerald-500"><Icon name="check" className="w-5 h-5" /></span>
                              : <span className="inline-flex text-gray-300"><Icon name="x" className="w-4 h-4" /></span>}
                          </td>
                        ))}
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Preguntas */}
      <section id="preguntas" className="bg-white border-y border-gray-100">
        <div className="max-w-3xl mx-auto px-5 py-16 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 text-center">Preguntas frecuentes</p>
          <h2 className="text-3xl font-black tracking-tight mt-2 text-center">Lo que suelen preguntarnos</h2>
          <div className="mt-8 divide-y divide-gray-100 rounded-3xl border border-gray-100 bg-slate-50/50">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group px-5 py-4">
                <summary className="flex items-center justify-between gap-3 cursor-pointer font-bold text-sm list-none">{q}<span className="text-gray-300 group-open:rotate-45 transition-transform text-xl leading-none">+</span></summary>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Prueba */}
      <section id="prueba" className="px-5 py-16 sm:py-24" style={{ background: 'linear-gradient(160deg,#0b1224 0%,#222d5a 100%)' }}>
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-white text-3xl font-black tracking-tight">{signup.open ? 'Creá tu cuenta gratis' : 'Pedí tu prueba gratuita'}</h2>
            <p className="text-slate-300 text-sm mt-2">{signup.open ? `${signup.days} días gratis, sin tarjeta. Te mandamos un mail para elegir tu clave y entrar.` : 'Contanos de tu organización y te dejamos todo listo para empezar.'}</p>
          </div>
          <div className="bg-white rounded-3xl shadow-2xl p-6">
            {state.done ? (
              <div className="text-center py-6">
                <span className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Icon name="check" className="w-6 h-6" /></span>
                <p className="font-bold text-gray-900 mt-3">{signup.open ? '¡Tu cuenta está lista!' : '¡Recibimos tu pedido!'}</p>
                <p className="text-sm text-gray-500 mt-1">{signup.open ? `Te enviamos un mail a ${form.email} para elegir tu clave. Si no lo ves, revisá el correo no deseado.` : 'Te escribimos en breve para dejar tu prueba lista.'}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <input required placeholder="Tu nombre" value={form.name} onChange={set('name')} className={input} />
                <input required placeholder="Nombre de tu organización, pyme o comercio" value={form.org} onChange={set('org')} className={input} />
                <input required type="email" placeholder="Tu email" value={form.email} onChange={set('email')} className={input} />
                <input required type="tel" minLength={8} placeholder="Teléfono o WhatsApp" value={form.phone} onChange={set('phone')} className={input} />
                <select value={plans.some((p) => p.id === form.plan) ? form.plan : plans[0]?.id} onChange={set('plan')} className={input}>
                  {plans.map((p) => <option key={p.id} value={p.id}>Plan {p.name} — {p.people}</option>)}
                </select>
                {!signup.open && <textarea placeholder="Contanos brevemente qué necesitás (opcional)" rows={3} maxLength={1000} value={form.message} onChange={set('message')} className={input} />}
                <input name="website" tabIndex={-1} autoComplete="off" value={form.website || ''} onChange={set('website')} className="hidden" aria-hidden="true" />
                {signup.open && <p className="text-[11px] text-gray-400">Al crear la cuenta aceptás los <a href="/terminos" className="underline">Términos</a> y la <a href="/privacidad" className="underline">Política de Privacidad</a>.</p>}
                {state.err && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{state.err}</p>}
                <button disabled={state.busy} className="w-full py-3.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={grad}>{state.busy ? (signup.open ? 'Creando tu cuenta…' : 'Enviando…') : (signup.open ? `Crear cuenta gratis · ${signup.days} días` : 'Quiero probarlo')}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="flex items-center gap-2"><img src={logoMark} alt="" className="w-6 h-6 rounded-lg" />{BRAND.product}</span>
          <span><a href="/terminos" className="hover:text-white">Términos y Condiciones</a> · <a href="/privacidad" className="hover:text-white">Política de Privacidad</a> · <button onClick={onLogin} className="hover:text-white">Ingresar</button></span>
        </div>
      </footer>
    </div>
  );
}
